// src/components/Header.js
import React, {useState, useEffect, useLayoutEffect, useRef} from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import menuStore from '../utils/localForageInstance'; 
import { callAPI } from '../utils/apiUtils';
import { apiUrls } from '../utils/apiUrls';
import { toast } from 'react-toastify';
import '../styles/headermenu.css';
import '../styles/callGrowl.css';
import OfflineBanner from './OfflineBanner';
import TomPendingBanner from './TomPendingBanner';
import { useConnection } from '../offline/connection';
import { staffLockAllowed } from '../offline/state';


const Header = ({ guardedNavigate }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const callIntervalRef = useRef(null);
    const shownCallsRef = useRef(new Set());
    const callEnabledRef = useRef(true);
    const headerRef = useRef(null);
    const { state: offlineState, engine: hasOfflineEngine, headerOnly: offlineHeaderOnly } = useConnection();

    // .pageContent's top padding and .cartSidebar/.categoryCol's sticky offset both read
    // --topSpace to sit below the fixed header - a hardcoded guess at its height drifts out of
    // sync as nav items get added (and rendered header height also varies by engine), so measure
    // the real thing instead and keep it live across resize/orientation/content changes.
    useLayoutEffect(() => {
        const el = headerRef.current;
        if (!el) return;
        const applyHeight = (height) => {
            document.documentElement.style.setProperty('--topSpace', `${height}px`);
        };
        applyHeight(el.getBoundingClientRect().height);
        // Re-measure via getBoundingClientRect (border-box) rather than the observer's own
        // contentRect, which excludes padding and would under-report .headerSection's real size.
        const observer = new ResizeObserver(() => applyHeight(el.getBoundingClientRect().height));
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    let callWrapper = document.getElementById('call-wrapper');

if (!callWrapper) {
    callWrapper = document.createElement('div');
    callWrapper.id = 'call-wrapper';
    document.body.appendChild(callWrapper);
}


    const getOrderText = () => {
        const orderType = localStorage.getItem('orderType') || 'takeaway';
        return orderType.toLowerCase() === 'dinein' ? 'Dine-in' : 'Takeaway / Delivery';
    };
    const getActiveClass = (activePaths) => {
        return activePaths.includes(location.pathname) ? 'navItem active' : 'navItem';
    };
      
    const [isMenuOpen, setIsMenuOpen] = useState(false);

const toggleMenu = () => {
    setIsMenuOpen(prev => !prev);
 };

 const closeMenu = () => {
    setIsMenuOpen(false);
};

    const handleNav = (e, to, useGuard = false, state = {}) => {
        if (useGuard && guardedNavigate) {
          e.preventDefault(); 
          guardedNavigate(to, { state });
        }
      };
    const restaurantName = localStorage.getItem("restaurant_name");
    const lname = localStorage.getItem("lname");
    const fname = localStorage.getItem("fname");
    const fullName = `${fname || ''} ${lname || ''}`.trim();

    const displayName = restaurantName || fullName;

    const hoverText = restaurantName && fullName 
    ? `${restaurantName} (${fullName})`
    : displayName;
    // Whoever was PIN-identified on this till is not identified any more once the restaurant
    // session ends or a switch is requested. Cleared together so a stale name can never outlive
    // the permission set it came with, or the other way round.
    const clearActiveStaff = () => {
        localStorage.removeItem('active_staff_id');
        localStorage.removeItem('active_staff_name');
        localStorage.removeItem('active_staff_role');
        localStorage.removeItem('active_staff_permissions');
    };

    // Hands off to StaffPinGate, which owns the prompt and is mounted by the layout on every
    // route - so this works from anywhere without the modal living in the header.
    const handleSwitchStaff = (e) => {
        e.preventDefault();
        // Offline with no PIN credentials on this device nobody could be let back in: leave whoever is signed in alone.
        if (!staffLockAllowed(offlineState)) {
            toast.info('Staff PINs are not available offline on this device yet. Register it from Offline mode while online.');
            return;
        }
        clearActiveStaff();
        window.dispatchEvent(new Event('staffSwitchRequested'));
    };

    const handleLogout = (e) => {
        e.preventDefault();
        localStorage.removeItem('accessToken');
        localStorage.removeItem('orderart_session_id'); // <-- remove session id
        //localStorage.removeItem('isTakeaway');
        localStorage.removeItem('order_id');
        localStorage.removeItem('orderType');
        clearActiveStaff();
        navigate('/');
    };
    const [syncLoading, setSyncLoading] = useState(false);
    // const handleTakeawayClick = (e) => {
    //     e?.preventDefault?.();
    //     localStorage.setItem('isTakeaway', 'true');
    //     navigate('/takeaway');
    // };
    const handleSyncMenu = async (e) => {
        e?.preventDefault?.();
        setSyncLoading(true);
        try {
        const [categoriesRes, dishesRes] = await Promise.all([
            callAPI(apiUrls.CATEGORY_LISTING, 'GET'),
            callAPI(apiUrls.MENU_DISHES, 'GET'),
        ]);
        const dishCategoryItems = categoriesRes.data?.data || [];
        const dishesData = dishesRes.data?.data || [];
        //iski ek common class banani hai date format ki ya  util me daal denge
        const lastSynced = new Date().toLocaleString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
        });
        await menuStore.setItem('dish_category_items', dishCategoryItems);
        await menuStore.setItem('last_synced_pos_data', lastSynced);
        await menuStore.setItem('dish_menu_items', true); 
    
        for (const product of dishesData) {
            if (product?.product_key?.startsWith('product_')) {
            await menuStore.setItem(product.product_key, product);
            console.log(`Stored ${product.product_key}`); // ye console me batayega ki kaun sync hua hai
            }
        }
        console.log('Menu data synced and saved locally');
        toast.success('Menu data synced successfully!');
        setTimeout(() => {
            window.location.reload();
          }, 2000); 
        } catch (error) {
        console.error('Error syncing menu:', error);
        toast.error('Error syncing menu data.');
        }finally {
        setSyncLoading(false);
        }
    };

    useEffect(() => {
        const fetchCalls = async () => {
            try {
                const response = await callAPI(apiUrls.CALL_FETCH, {}, 'GET', null, {}, {}, false);
    
                const isEnabled = response?.data?.call_notifications_enabled;
                const finalEnabled = Number(isEnabled) != 0;
    
                if (!finalEnabled) {
                    callEnabledRef.current = false;
    
                    if (callIntervalRef.current) {
                        clearInterval(callIntervalRef.current);
                        callIntervalRef.current = null;
                    }
                    return;
                }
    
                callEnabledRef.current = true;
    
                const calls = response?.data?.data || [];
    
                calls.forEach(call => {
                    if (shownCallsRef.current.has(call.id)) return;
    
                    shownCallsRef.current.add(call.id);
                    showCallNotification(call);
                });
    
            } catch (err) {
                console.error('Call polling error:', err);
            }
        };
    
        fetchCalls();
    
        if (!callIntervalRef.current) {
            callIntervalRef.current = setInterval(fetchCalls, 5000);
        }
    
        return () => clearInterval(callIntervalRef.current);
    }, []);

    const handleOpenDrawer = async (e) => {
        e.preventDefault();
    
        try {
            const response = await callAPI(
                apiUrls.OPEN_DRAWER, 
                {}, 
                'GET'
            );
    
            if (response.data?.status == 1) {
                toast.success(response.message || 'Cash drawer opened successfully!');
            } else {
                toast.error(response.message || 'Failed to open cash drawer');
            }
    
        } catch (error) {
            toast.error('Something went wrong while opening cash drawer');
            console.error(error);
        }
    };


    // "Dismiss all" shows above the alerts once there are two or more on screen.
    const updateDismissAll = () => {
        const count = callWrapper.querySelectorAll('.call-notification').length;
        let btn = document.getElementById('call-dismiss-all');
        if (count < 2) {
            if (btn) btn.remove();
            return;
        }
        if (!btn) {
            btn = document.createElement('button');
            btn.id = 'call-dismiss-all';
            btn.className = 'call-dismiss-all';
            btn.onclick = dismissAllCalls;
            callWrapper.insertBefore(btn, callWrapper.firstChild);
        }
        btn.textContent = `Dismiss all (${count})`;
    };

    const removeCallCard = (container) => {
        container.remove();
        updateDismissAll();
    };

    const dismissAllCalls = () => {
        callWrapper.querySelectorAll('.call-notification').forEach(card => {
            const id = Number(card.dataset.callId);
            card.remove();
            callAPI(apiUrls.CALL_MARK_DISMISSED, {}, 'POST', { id }, {}, {}, false)
                .catch(err => console.error('CALL_MARK_DISMISSED failed:', err));
        });
        updateDismissAll();
    };

    const showCallNotification = (call) => {
        const id = call.id;
    
        const container = document.createElement('div');
        container.id = `call-notification-${id}`;
        container.className = 'call-notification';
        container.dataset.callId = id;
    
        const hasView =  !!call.link;

        container.innerHTML = `
        <div class="call-box">
    
            <div class="call-header">
                Incoming Call
            </div>
    
            <div class="call-body">
                <div class="call-name">${call.customer || 'Unknown'}</div>
    
                <div class="call-phone">
                    
                    <!-- GREEN PHONE SVG -->
                    <svg class="call-icon" viewBox="0 0 24 24">
                        <path fill="currentColor" d="M6.6 10.79a15.05 15.05 0 006.61 6.61l2.2-2.2a1 1 0 011.02-.24c1.12.37 2.33.57 3.57.57a1 1 0 011 1V20a1 1 0 01-1 1C10.61 21 3 13.39 3 4a1 1 0 011-1h3.5a1 1 0 011 1c0 1.24.2 2.45.57 3.57a1 1 0 01-.25 1.02l-2.22 2.2z"/>
                    </svg>
    
                    ${call.number || ''}
                </div>
            </div>
    
            <div class="call-actions ${hasView ? 'has-view' : 'no-view'}">
    
                ${hasView ? `
                    <button class="btn btn-view" id="view-${id}">
                        View
                    </button>
                ` : ''}
    
                <button class="btn btn-takeaway" id="takeaway-${id}">
                    Take Away
                </button>
    
                <button class="btn btn-reservation" id="reservation-${id}">
                    Reservation
                </button>
    
                <button class="btn btn-dismiss" id="dismiss-${id}">
                    Dismiss
                </button>
    
            </div>
    
        </div>
    `;
        callWrapper.appendChild(container);
        updateDismissAll();
    
        // auto mark sent (same as PHP)
        setTimeout(() => {
            callAPI(apiUrls.CALL_MARK_SENT, {},'POST', { id },  {},  {}, false);
        }, 2000);
    
        // handlers
        const dismissBtn = document.getElementById(`dismiss-${id}`);
        const viewBtn = document.getElementById(`view-${id}`);
        const takeawayBtn = document.getElementById(`takeaway-${id}`);
        const reservationBtn = document.getElementById(`reservation-${id}`);
        
        if (dismissBtn) {
            dismissBtn.onclick = () => {
                removeCallCard(container);
                callAPI(apiUrls.CALL_MARK_DISMISSED, {}, 'POST', { id }, {}, {}, false)
                    .catch(err => console.error('CALL_MARK_DISMISSED failed:', err));
            };
        }

        if (viewBtn) {
            viewBtn.onclick = () => {
                removeCallCard(container);
                window.open(`/customer/profile?id=${call.user_id}`, '_blank');
                callAPI(apiUrls.CALL_MARK_COMPLETED, {}, 'POST', { id }, {}, {}, false)
                    .catch(err => console.error('CALL_MARK_COMPLETED failed:', err));
            };
        }
        
        if (takeawayBtn) {
            takeawayBtn.onclick = () => {
                navigate('/takeaway', {
                    state: { callId: id }
                });
                removeCallCard(container);
            };
        }
        
        if (reservationBtn) {
            reservationBtn.onclick = () => {
                navigate('/reservations', {
                    state: { callId: id }
                });
                removeCallCard(container);
            };
        }
    };

    return (
        <header className="headerSection" ref={headerRef}>
            <div className="container">
                <div className="headerRow">
                    <div className="row position-relative z-2 align-items-center  position-relative">
                        <div className="col-auto">
                            <Link to="/dashboard" onClick={(e) => handleNav(e, '/dashboard', true)} className="logoCol">
                                <svg width="55" height="56" viewBox="0 0 55 56" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M30.0901 40.0787V44.1883C36.5836 42.0513 39.8062 35.524 39.8062 35.524V28.5103C39.8062 21.9418 34.8483 16.1747 28.2653 15.7295C20.6219 15.2158 14.3074 21.6336 15.058 29.2774C15.4987 33.7705 18.5836 37.6883 22.7772 39.4281C25.3663 40.5171 27.8315 40.5787 30.0901 40.0787ZM24.7122 28.0377C24.7122 26.5651 25.9172 25.3664 27.3977 25.3664C28.8782 25.3664 30.0832 26.5651 30.0832 28.0377C30.0832 29.5103 28.8782 30.7089 27.3977 30.7089C25.9172 30.7089 24.7122 29.5171 24.7122 28.0377Z" fill="currentColor"/>
                                    <path d="M27.3971 0.907471C12.3581 0.907471 0.121704 13.0787 0.121704 28.0376C0.121704 42.9964 12.3581 55.1676 27.3971 55.1676C31.8661 55.1676 36.0734 54.0718 39.7987 52.1676V49.6882V44.5992V40.38C37.788 42.38 35.2815 43.8937 32.4927 44.7362C31.7146 44.9759 30.902 45.1403 30.0826 45.2636C29.2012 45.4005 28.3129 45.4896 27.3971 45.4896C17.7154 45.4896 9.83782 37.654 9.83782 28.0239C9.83782 18.3937 17.7154 10.5581 27.3971 10.5581C37.0788 10.5581 44.9563 18.3937 44.9563 28.0239C44.9563 30.9211 44.2333 33.654 42.9732 36.0581V50.2635C50.0382 45.3526 54.6724 37.2225 54.6724 28.017C54.6793 13.0787 42.4429 0.907471 27.3971 0.907471Z" fill="currentColor"/>
                                </svg>
                            </Link>
                        </div>
                        <div className="col-auto ms-auto order-lg-last">
                            <div className="d-flex gap-3 align-items-center  position-relative"  >
                                <div className="btn-group">
                                    <span title={hoverText} className="hProfileTrigger" data-bs-toggle="dropdown" aria-expanded="false">
                                        <img src={`${process.env.PUBLIC_URL}/images/profile.webp`} alt="Profile" className="profileImg" />
                                        <span className="uName">{displayName}</span>
                                        <svg width="19" height="12" className="ddArrow">
                                            <use href={`${process.env.PUBLIC_URL}/images/sprite.svg#iconDropdownArrow`} />
                                        </svg>
                                    </span>
                                    
                                    <ul className="dropdown-menu ddMenuStyle dropdown-menu-end">
                                        <li>
                                        <Link
                                        to="/order-transfer"
                                        className="dropdown-item"
                                        onClick={(e) => handleNav(e, '/order-transfer', true)}
                                        >
                                                <svg width="24" height="24" className="ddItemIcon">
                                                    <use href={`${process.env.PUBLIC_URL}/images/sprite.svg#iconTransferOptions`} />
                                                </svg>
                                                <span>Transfer Options</span>
                                            </Link>
                                        </li>
                                        <li>
                                            <Link to="/customer" className="dropdown-item" onClick={(e) => handleNav(e, '/customer', true)}>
                                                <svg width="24" height="24" className="ddItemIcon">
                                                    <use href={`${process.env.PUBLIC_URL}/images/sprite.svg#iconCustomers`} />
                                                </svg>
                                                <span>Customers</span>
                                            </Link>
                                        </li>
                                        <li>
                                        <Link
                                        to="/promotions"
                                        className="dropdown-item"
                                        onClick={(e) => handleNav(e, '/promotions', true)}
                                        >
                                                <svg width="24" height="24" className="ddItemIcon">
                                                    <use href={`${process.env.PUBLIC_URL}/images/sprite.svg#iconPromotions`} />
                                                </svg>
                                                <span>Promotions</span>
                                            </Link>
                                        </li>
                                        <li>
                                        <Link
                                        to="/floor-plan"
                                        className="dropdown-item"
                                        onClick={(e) => handleNav(e, '/floor-plan', true)}
                                        >
                                                <svg width="24" height="24" className="ddItemIcon">
                                                    <use href={`${process.env.PUBLIC_URL}/images/sprite.svg#iconIndoorSeating`} />
                                                </svg>
                                                <span>Floor Plan</span>
                                            </Link>
                                        </li>
                                        <li>
                                        <Link
                                        to="/product-management"
                                        className="dropdown-item"
                                        onClick={(e) => handleNav(e, '/product-management', true)}
                                        >
                                                <svg width="24" height="24" className="ddItemIcon">
                                                    <use href={`${process.env.PUBLIC_URL}/images/sprite.svg#iconMenu`} />
                                                </svg>
                                                <span>Product Management</span>
                                            </Link>
                                        </li>
                                        <li>
                                            <Link  onClick={handleOpenDrawer} className="dropdown-item">
                                                <svg width="24" height="24" className="ddItemIcon">
                                                    <use href={`${process.env.PUBLIC_URL}/images/sprite.svg#iconCash`} />
                                                </svg>
                                                <span>Cash Drawer</span>
                                            </Link>
                                        </li>
                                        <li>
                                            <Link to="/clockin-clockout" className="dropdown-item" onClick={(e) => handleNav(e, '/clockin-clockout', true)}>
                                                <svg width="24" height="24" className="ddItemIcon">
                                                    <use href={`${process.env.PUBLIC_URL}/images/sprite.svg#iconTimesheets`} />
                                                </svg>
                                                <span>Timesheets</span>
                                            </Link>
                                        </li>
                                        <li>
                                        <Link
                                        to="/revenue"
                                        className="dropdown-item"
                                        onClick={(e) => handleNav(e, '/revenue', true)}
                                        >
                                                <svg width="24" height="24" className="ddItemIcon">
                                                    <use href={`${process.env.PUBLIC_URL}/images/sprite.svg#iconEOBRevenue`} />
                                                </svg>
                                                <span>EOB Revenue</span>
                                            </Link>
                                        </li>
                                        <li>
                                            <Link to="/hardware" className="dropdown-item" onClick={(e) => handleNav(e, '/hardware', true)}>
                                                <svg width="24" height="24" className="ddItemIcon" viewBox="0 0 24 24" fill="none">
                                                    <path
                                                        d="M6 9V3h12v6M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2M6 14h12v7H6z"
                                                        stroke="currentColor"
                                                        strokeWidth="2"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    />
                                                </svg>
                                                <span>Hardware</span>
                                            </Link>
                                        </li>
                                        {(hasOfflineEngine || offlineHeaderOnly) && (
                                        <li>
                                            <Link to="/offline" className="dropdown-item" onClick={(e) => handleNav(e, '/offline', true)}>
                                                <svg width="24" height="24" className="ddItemIcon" viewBox="0 0 24 24" fill="none">
                                                    <path
                                                        d="M2 8.8a15 15 0 0 1 4-2.4M22 8.8a15 15 0 0 0-6.4-3.6M5 12.5a10 10 0 0 1 2.7-1.8M19 12.5a10 10 0 0 0-5-2.6M8.5 16.4a5 5 0 0 1 7 0M12 20h.01M2 2l20 20"
                                                        stroke="currentColor"
                                                        strokeWidth="2"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    />
                                                </svg>
                                                <span>Offline mode</span>
                                            </Link>
                                        </li>
                                        )}
                                        <li>
                                            <Link to="/operating-hours" className="dropdown-item" onClick={(e) => handleNav(e, '/operating-hours', true)}>
                                                <svg width="24" height="24" className="ddItemIcon" viewBox="0 0 24 24" fill="none">
                                                    <path
                                                        d="M12 22a10 10 0 100-20 10 10 0 000 20zM12 6v6l4 2"
                                                        stroke="currentColor"
                                                        strokeWidth="2"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    />
                                                </svg>
                                                <span>Operating Hours</span>
                                            </Link>
                                        </li>
                                        <li>
                                            <Link to="/settings" className="dropdown-item" onClick={(e) => handleNav(e, '/settings', true)}>
                                                <svg width="24" height="24" className="ddItemIcon" viewBox="0 0 24 24" fill="none">
                                                    <path
                                                        d="M12 15a3 3 0 100-6 3 3 0 000 6z"
                                                        stroke="currentColor"
                                                        strokeWidth="2"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    />
                                                    <path
                                                        d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 11-4 0v-.09a1.65 1.65 0 00-1-1.51 1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 110-4h.09a1.65 1.65 0 001.51-1 1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 114 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 110 4h-.09a1.65 1.65 0 00-1.51 1z"
                                                        stroke="currentColor"
                                                        strokeWidth="2"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    />
                                                </svg>
                                                <span>Settings</span>
                                            </Link>
                                        </li>
                                        <li>
                                            <a href="/" className="dropdown-item" onClick={handleLogout}>
                                                <svg width="24" height="24" className="ddItemIcon">
                                                    <use href={`${process.env.PUBLIC_URL}/images/sprite.svg#iconLogout`} />
                                                </svg>
                                                <span>Logout</span>
                                            </a>
                                        </li>
                                    </ul>
                                </div>
                                <div id="hamburger" className={`hamburger d-lg-none ${isMenuOpen ? 'actNav open' : ''}`} onClick={toggleMenu}>
                                    <span className="bar bar1"></span>
                                    <span className="bar bar2"></span>
                                    <span className="bar bar3"></span>
                                    <span className="bar bar4"></span>
                                </div>
                            </div>
                        </div>
                        <div className="col-lg d-lg-flex justify-content-center">
                            <div className={`menuColMain ${isMenuOpen ? 'show' : ''}`}>
                                <ul className="navStyle">
                                    <li>
                                        <Link to="/takeaway" state={{ orderType: 'takeaway' }}     className={getActiveClass(['/takeaway', '/order-review','/select-customer','/payment-view'])}
                                        onClick={(e) => {
                                            // This plain nav entry has no table/zone of its own, but
                                            // Takeaway.js falls back to this leftover value when
                                            // location.state carries no tabId - so a Pager tapped
                                            // earlier in the session otherwise keeps isPagerOrder true
                                            // here too, showing its Quick Checkout button alongside
                                            // this screen's own (see useTableOrdering.js/Takeaway.js).
                                            localStorage.removeItem('selectedTabId');
                                            handleNav(e, '/takeaway', true);
                                        }}
                                        >
                                            <svg width="41" height="41" className="navIcon">
                                                <use href={`${process.env.PUBLIC_URL}/images/sprite.svg#iconTakeaway`} />
                                            </svg>
                                            <span className="navText">{getOrderText()}</span>
                                        </Link>
                                    </li>
                                    <li>
                                    <Link
                                        to="/finalize-order"
                                        className={getActiveClass(['/finalize-order'])}
                                        
                                        onClick={(e) => handleNav(e, '/finalize-order', true)}
                                        >
                                                <svg width="41" height="41" className="navIcon">
                                                    <use href={`${process.env.PUBLIC_URL}/images/sprite.svg#iconFinalizeOrder`} />
                                                </svg>
                                                <span className="navText">Finalise Table</span>
                                        </Link>
                                    </li>
                                    <li>
                                    <Link
                                        to="/reservations"
                                        className={getActiveClass(['/reservations'])}
                                        onClick={(e) => handleNav(e, '/reservations', true)}
                                        >
                                            <svg width="41" height="41" className="navIcon">
                                                <use href={`${process.env.PUBLIC_URL}/images/sprite.svg#iconReservations`} />
                                            </svg>
                                            <span className="navText">Reservations</span>
                                        </Link>
                                    </li>
                                    <li>
                                        <button type="button" className="navItem" onClick={handleSyncMenu} style={{ background: 'none', border: 'none', padding: 0 }}>
                                            <svg width="41" height="41" className="navIcon">
                                                <use href={`${process.env.PUBLIC_URL}/images/sprite.svg#iconMenu`} />
                                            </svg>
                                            <span className="navText">Sync Menu</span>
                                        </button>
                                    </li>
                                    <li>
                                    <Link
                                        to="/orders"
                                        className={getActiveClass(['/orders'])}
                                        onClick={(e) => handleNav(e, '/orders', true)}
                                        >
                                            <svg width="41" height="41" className="navIcon">
                                                <use href={`${process.env.PUBLIC_URL}/images/sprite.svg#iconOrder`} />
                                            </svg>
                                            <span className="navText">All Orders</span>
                                        </Link>
                                    </li>
                                    <li>
                                    <Link
                                        to="/ubereats/orders"
                                        className={getActiveClass(['/ubereats/orders'])}
                                        onClick={(e) => handleNav(e, '/ubereats/orders', true)}
                                        >
                                            <svg width="41" height="41" className="navIcon">
                                                <use href={`${process.env.PUBLIC_URL}/images/sprite.svg#iconUber`} />
                                            </svg>
                                            <span className="navText">Uber</span>
                                        </Link>
                                    </li>
                                    <li>
                                        <button type="button" className="navItem" onClick={handleSwitchStaff} style={{ background: 'none', border: 'none', padding: 0 }}>
                                            <svg width="41" height="41" className="navIcon">
                                                <use href={`${process.env.PUBLIC_URL}/images/sprite.svg#iconTimesheets`} />
                                            </svg>
                                            <span className="navText">Switch User</span>
                                        </button>
                                    </li>
                                </ul>
                            </div>
                            <div 
  className={`menuBackdrop ${isMenuOpen ? 'show' : ''}`}
  onClick={closeMenu}
/>
                        </div>
                    </div>
                </div>
            </div>
            <OfflineBanner onNavigate={(e) => handleNav(e, '/offline', true)} />
            <TomPendingBanner />
            {syncLoading && (
                <div className="orderart-fullpage-loader">
                    <div className="orderart-loader-content">
                        <svg className="orderart-spinner" width="60" height="60" viewBox="0 0 50 50">
                            <circle className="path" cx="25" cy="25" r="20" fill="none" stroke="currentColor" strokeWidth="5" />
                        </svg>
                        <div className="orderart-loader-text">Syncing Menu...<br /><b>OrderArt</b></div>
                    </div>
                </div>
            )}
        </header>
    );
};

export default Header;
